/**
 * MODULE: _shared/frontend-shell/src/shell/components/UserMenu.tsx
 * PURPOSE: Avatar account menu — profile, workspace settings, theme, shortcuts help, sign out
 * DEPENDENCIES: react-router-dom, flowbite-react Dropdown/Avatar, @core/auth useAuth, ../hooks/useTheme
 * PUBLIC_API: UserMenu, UserMenuUser
 * SIDE_EFFECTS: navigate, logout, theme DOM + localStorage, optional shortcuts popover
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-05-22 [2.2.1132] — dropdown **`z-[10001]`** (#116 CRM user menu above page content). [2.2.1132]
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown, Avatar } from 'flowbite-react';
import { useAuth } from '@core/auth';
import { useTheme, type ThemeMode } from '../hooks/useTheme';

export type UserMenuUser = {
  name?: string;
  email?: string;
  avatar_url?: string;
};

export type UserMenuProps = {
  user?: UserMenuUser;
  /** `topbar` uses Flowbite Avatar; `hub` matches Sales Hub circular trigger */
  variant?: 'topbar' | 'hub';
  profileTo?: string;
  workspaceTo?: string;
};

function useInitials(user?: UserMenuUser): string {
  return useMemo(() => {
    const n = user?.name?.trim() || user?.email?.trim() || '';
    if (!n) return '?';
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]![0]!}${parts[1]![0]!}`.toUpperCase();
    }
    return n.slice(0, 2).toUpperCase();
  }, [user?.name, user?.email]);
}

export function UserMenu({
  user,
  variant = 'topbar',
  profileTo = '/settings/profile',
  workspaceTo = '/settings/workspace',
}: UserMenuProps) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const initials = useInitials(user);

  const onSignOut = useCallback(async () => {
    await logout();
    navigate('/auth', { replace: true });
  }, [logout, navigate]);

  const setAppearance = (mode: ThemeMode) => {
    setTheme(mode);
  };

  if (!user) {
    return null;
  }

  // Flowbite Dropdown wraps `label` in a focusable control — avoid nested <button>.
  const trigger =
    variant === 'hub' ? (
      <span
        data-testid="user-menu-trigger"
        aria-label="Account menu"
        className="ml-1 flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-gray-200 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 transition hover:ring-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:bg-gray-700 dark:text-gray-100 dark:ring-gray-600"
        title={user.name || user.email || 'Account'}
      >
        {user.avatar_url ? (
          <img src={user.avatar_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center">{initials}</span>
        )}
      </span>
    ) : (
      <span
        data-testid="user-menu-trigger"
        aria-label="Account menu"
        className="inline-flex cursor-pointer rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <Avatar
          img={user.avatar_url}
          alt={user.name || 'User'}
          rounded
          size="sm"
          placeholderInitials={user.name?.charAt(0) || 'U'}
        />
      </span>
    );

  return (
    <Dropdown
      label={trigger}
      arrowIcon={false}
      inline
      theme={{
        floating: {
          base: 'z-[10001] w-fit divide-y divide-gray-100 rounded shadow focus:outline-none',
          item: {
            base: 'flex cursor-pointer justify-start px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:text-gray-200 dark:hover:bg-gray-600 dark:hover:text-white dark:focus:bg-gray-600 dark:focus:text-white',
          },
          style: {
            dark: 'bg-gray-700 text-white',
            light: 'bg-white text-gray-900',
            auto: 'bg-white text-gray-900 dark:bg-gray-700 dark:text-white',
          },
        },
      }}
    >
      <div className="px-4 py-3 text-sm text-gray-900 dark:text-white">
        <div className="font-medium">{user.name || 'User'}</div>
        <div className="truncate text-sm text-gray-500 dark:text-gray-400">{user.email || ''}</div>
      </div>

      <Link
        to={profileTo}
        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
        role="menuitem"
      >
        Profile
      </Link>
      <Link
        to={workspaceTo}
        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
        role="menuitem"
      >
        Workspace settings
      </Link>

      <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Theme
      </div>
      <div className="flex flex-wrap gap-1 px-4 pb-2">
        {(['light', 'dark', 'system'] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            className={`rounded-md px-2 py-1 text-xs font-medium capitalize ring-1 transition ${
              theme === mode
                ? 'bg-primary-100 text-primary-800 ring-primary-300 dark:bg-primary-900/40 dark:text-primary-200 dark:ring-primary-700'
                : 'bg-gray-50 text-gray-700 ring-gray-200 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-200 dark:ring-gray-600 dark:hover:bg-gray-700'
            }`}
            onClick={() => setAppearance(mode)}
          >
            {mode}
          </button>
        ))}
      </div>

      <div className="relative px-4 pb-2">
        <button
          type="button"
          className="w-full rounded-md px-2 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
          aria-expanded={shortcutsOpen}
          data-testid="user-menu-keyboard-shortcuts"
          onClick={() => setShortcutsOpen((o) => !o)}
        >
          Keyboard shortcuts
        </button>
        {shortcutsOpen && (
          <div
            className="absolute left-4 right-4 z-50 mt-1 rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-700 shadow-lg dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            role="dialog"
            aria-label="Keyboard shortcuts"
          >
            <ul className="space-y-2">
              <li>
                <span className="font-medium">⌘K</span> — Assistant (toggle overlay or focus on Assistant page)
              </li>
              <li>
                <span className="font-medium">⌘⇧K</span> — Global search
              </li>
              <li>
                <span className="font-medium">⌘⇧I</span> — Sales intelligence
              </li>
              <li>
                <span className="font-medium">⌘J</span> — Main assistant (FAB) where available
              </li>
            </ul>
            <button
              type="button"
              className="mt-2 w-full text-right text-primary-600 hover:underline dark:text-primary-400"
              onClick={() => setShortcutsOpen(false)}
            >
              Close
            </button>
          </div>
        )}
      </div>

      <hr className="my-1 h-px bg-gray-100 border-0 dark:bg-gray-700" />

      <button
        type="button"
        role="menuitem"
        className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
        data-testid="user-menu-sign-out"
        onClick={() => void onSignOut()}
      >
        Sign out
      </button>
    </Dropdown>
  );
}
